// docs/116: the inn's action bar, a small wooden shelf of things to do under the village's top bar.
// Each place walks my band there and the place opens its panel on arrival (InnLayer's `go`); a toast
// is raised where the band stands. Upstairs it is only the way back down and a word about the beds.
import { useEffect, useState } from 'react';
import { playSfx } from '../../../../../core/audio';
import { deviceNow } from '../../../../../core/time';
import { useInn, type InnPanel } from '../store';
import type { InnData } from '../types';

export const TOAST_COOLDOWN_MS = 10_000;

interface Item { id: string; label: string; glyph: string; title: string; spot?: string; panel?: InnPanel }

function goTo(spot: string, panel: InnPanel): void {
  playSfx('ui.click');
  const go = useInn.getState().go;
  if (go) go(spot);
  else useInn.getState().open(panel);
}

export function InnBar({ data }: { data: InnData }): React.JSX.Element {
  const panel = useInn((s) => s.panel);
  const [until, setUntil] = useState(0);
  const [now, setNow] = useState(() => deviceNow());
  useEffect(() => {
    if (until <= now) return;
    const t = window.setTimeout(() => setNow(deviceNow()), 250);
    return () => window.clearTimeout(t);
  }, [until, now]);
  const wait = Math.max(0, Math.ceil((until - now) / 1000));

  const toast = async () => {
    if (wait > 0) return;
    const t = deviceNow();
    setUntil(t + TOAST_COOLDOWN_MS); setNow(t);
    myToast.at = t;
    if (!(await data.act.toast())) { setUntil(0); playSfx('ui.error'); }
  };

  const items: Item[] = [
    { id: 'drink', label: 'Order a drink', glyph: '♨', title: 'Walk to the bar and order a drink', spot: 'bar', panel: 'bar' },
    { id: 'song', label: 'Ask for a song', glyph: '♪', title: 'Ask the bard for a tune', spot: 'bard', panel: 'bard' },
    { id: 'dice', label: 'Hearth Bones', glyph: '⚂', title: data.dice?.state === 'playing' ? 'A game is on at the dice table' : 'Sit in at the dice table, or watch', spot: 'dice', panel: 'dice' },
    { id: 'notice', label: 'Noticeboard', glyph: '✉', title: 'Read the noticeboard by the door', spot: 'notice', panel: 'notice' },
    { id: 'book', label: 'Guestbook', glyph: '✎', title: 'Read and sign the guestbook', spot: 'lectern', panel: 'guestbook' },
    { id: 'room', label: data.rented ? 'Go upstairs' : 'Rent a room', glyph: '☾', title: data.rented ? 'Up to your room' : 'Rent a room for the night', spot: 'stairs', panel: 'room' },
  ];
  return (
    <nav className="fk-innp__bar fk-hit" aria-label="Things to do in the inn" data-testid="inn-bar">
      <button type="button" className={`fk-innp__btn fk-innp__btn--toast ${wait ? 'is-waiting' : ''}`} onClick={() => void toast()} disabled={wait > 0}
        title={wait ? `Again in ${wait}s` : 'Raise your mug to the room'} data-testid="inn-bar-toast">
        <span className="fk-innp__glyph" aria-hidden="true">⚱</span>
        <span className="fk-innp__label">{wait ? `Toast again in ${wait}s` : 'Raise a toast'}</span>
        {wait > 0 && <span className="fk-innp__cool" style={{ width: `${(wait * 1000 / TOAST_COOLDOWN_MS) * 100}%` }} aria-hidden="true" />}
      </button>
      {items.map((it) => (
        <button key={it.id} type="button" className={`fk-innp__btn ${it.panel && panel === it.panel ? 'is-on' : ''}`} title={it.title} data-testid={`inn-bar-${it.id}`}
          onClick={() => goTo(it.spot!, it.panel!)}>
          <span className="fk-innp__glyph" aria-hidden="true">{it.glyph}</span>
          <span className="fk-innp__label">{it.label}</span>
        </button>
      ))}
    </nav>
  );
}

/** When I last raised a toast (my own cheer coming back is not news to me). */
export const myToast = { at: 0 };

export function UpstairsBar({ data }: { data: InnData }): React.JSX.Element {
  return (
    <nav className="fk-innp__bar fk-innp__bar--up fk-hit" aria-label="Your room upstairs" data-testid="inn-bar">
      <span className="fk-innp__hint">Click a bed to sleep until morning.</span>
      <button type="button" className="fk-innp__btn" data-testid="inn-bar-down" onClick={() => { playSfx('inn.stairs'); data.act.upstairs(false); }}>
        <span className="fk-innp__glyph" aria-hidden="true">⇣</span>
        <span className="fk-innp__label">Go back down</span>
      </button>
    </nav>
  );
}
